// UPPDRAG-19 PR-A — global auth-required gate.
//
// Provides a programmatic way for any component (deep in the player or
// admin tree) to say "this action needs an account; show the conversion
// modal". Used by PR-B (anonymous schedule) + PR-C (anonymous open-match
// list) call-sites to gate "Boka tid" / "Anmäl till match" clicks.
//
// Architecture:
//   - <AuthRequiredProvider> mounts ONCE high in the tree (App.tsx) and
//     renders <AuthRequiredModal> as a child. The modal is invisible
//     until a consumer triggers it.
//   - useAuthRequired() is the only API consumers need. It returns
//     { requireAuth({ intent, returnUrl? }), dismiss(), state }.
//   - state is exposed for tests + edge-case consumers; normal callers
//     just use requireAuth().
//
// Why context instead of zustand: keeps the dependency surface small
// (zustand isn't in this codebase, no need to add it). The provider's
// state is intentionally tiny (3 fields), so re-render cost is fine.

import { createContext, useContext, useState, useCallback, ReactNode } from "react";
import AuthRequiredModal from "@/components/auth/AuthRequiredModal";

/**
 * What is the user trying to do? Drives the body copy in the modal.
 * Add new intents as PR-B / PR-C land — keep the union closed so
 * callers can't pass typos.
 */
export type AuthRequiredIntent =
  | "book_slot"
  | "join_open_match"
  | "register_team"
  | "join_tournament"
  | "generic";

export interface AuthRequiredState {
  isOpen: boolean;
  intent: AuthRequiredIntent;
  /** Where to send the user after a successful auth. Null until a trigger fires. */
  returnUrl: string | null;
}

export interface AuthRequiredContextValue {
  state: AuthRequiredState;
  /**
   * Opens the modal. If `returnUrl` is omitted, falls back to the
   * current `window.location.pathname + search` so the user lands back
   * on the same page after auth.
   */
  requireAuth: (args: { intent: AuthRequiredIntent; returnUrl?: string }) => void;
  /** Closes the modal without taking action. Same as ESC / outside-click. */
  dismiss: () => void;
}

const AuthRequiredContext = createContext<AuthRequiredContextValue | null>(null);

const INITIAL_STATE: AuthRequiredState = {
  isOpen: false,
  intent: "generic",
  returnUrl: null,
};

export function AuthRequiredProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthRequiredState>(INITIAL_STATE);

  const requireAuth = useCallback(
    (args: { intent: AuthRequiredIntent; returnUrl?: string }) => {
      const fallback =
        typeof window !== "undefined"
          ? window.location.pathname + window.location.search
          : "/";
      setState({
        isOpen: true,
        intent: args.intent,
        returnUrl: args.returnUrl ?? fallback,
      });
    },
    [],
  );

  const dismiss = useCallback(() => {
    // Keep intent + returnUrl so test assertions / re-opens see a stable
    // last-state. Only flip isOpen.
    setState((s) => ({ ...s, isOpen: false }));
  }, []);

  return (
    <AuthRequiredContext.Provider value={{ state, requireAuth, dismiss }}>
      {children}
      <AuthRequiredModal />
    </AuthRequiredContext.Provider>
  );
}

/**
 * Consumer hook. Must be called inside <AuthRequiredProvider>.
 * Throws otherwise so misuse is loud at dev time.
 */
export function useAuthRequired(): AuthRequiredContextValue {
  const ctx = useContext(AuthRequiredContext);
  if (!ctx) {
    throw new Error(
      "useAuthRequired must be used within <AuthRequiredProvider>. Mount the provider in App.tsx (UPPDRAG-19 PR-A).",
    );
  }
  return ctx;
}

// Re-export the modal so call-sites don't have to import the path
// separately. Most consumers only need `useAuthRequired`.
export { default as AuthRequiredModal } from "@/components/auth/AuthRequiredModal";
